import { useRef, useEffect, useMemo } from 'react'

import { makeGetRowState } from '../../utils/unitTableSettings.js'
import { buildColumnDefs } from './buildColumnDefs.jsx'

import { RecordTable }   from '../../../../shared/ui/RecordTable/index.js'

import styles from './ProductList.module.scss'

// ── Row helpers ────────────────────────────────────────────────────────────────

export function getRowKey(p) {
  return p.uuid ?? p.id_cad ?? p.crm_id_unit
}

// getRowState is a closure created per-render via makeGetRowState(accessibility).
// See unitTableSettings.js for the factory and the full state matrix.

// ── Component ──────────────────────────────────────────────────────────────────

/**
 * ProductList — RecordTable consumer for the catalog domain.
 *
 * Column order, visibility, actions and inquiry are fully driven by `panelSettings`.
 * Scrolls the active row into view whenever `activeProductKey` changes.
 *
 * @param {object}   props
 * @param {Array}    props.filteredProducts              - Pre-sorted product array
 * @param {object}   props.panelSettings         - Full panelSettings object from useApp()
 * @param {boolean}  [props.hasMultipleBuildings=false]
 * @param {boolean}  [props.hasMultipleGates=false]
 * @param {{ pricesVisible: boolean, pricesSoldVisible: boolean, pricesReservedVisible: boolean }} [props.pricesVisibility]
 * @param {Function} [props.isFavorite]          - (uuid) => boolean, from useApp()
 * @param {Function} [props.toggleFavorite]      - (uuid) => void, from useApp()
 * @param {string|null} [props.activeProductKey] - uuid or id_cad of the currently selected product
 * @param {Function} [props.onProductClick]      - (product) => void
 * @param {Array}    [props.sortConfig]           - SortLevel[] from useFilters
 * @param {Function} [props.onSortChange]         - (newSortConfig: SortLevel[]) => void
 * @param {string}   [props.noDataText]
 */
export function ProductList({
  activeActions         = [],
  activeProductKey      = null,
  filteredProducts      = [],
  hasMultipleBuildings  = false, //TODO: does it make sense to forcibly REMOVE column based on this flag if we allow client to set visibility in settings?
  hasMultipleGates      = false,
  isFavorite,
  listColumns           = [],
  noDataText            = "No units matching the criteria",
  onProductClick,
  onSortChange,
  pricesVisibility      = {},
  reservedUnitsAccessible,
  soldUnitsAccessible,
  sortConfig            = [],
  stickyColumns         = 1,
  tiers,
  toggleFavorite,
  viewMode              = 'list',

  panelSettings        = {},
}) {
  const containerRef = useRef(null)

  const getRowState = makeGetRowState({
    reservedUnitsAccessible,
    soldUnitsAccessible
  })

  const displayContext = useMemo(() => ({
    activeActions,
    hasMultipleBuildings,
    hasMultipleGates,
		isFavorite,
		onProductClick,
    pricesVisibility,
    tiers,
		toggleFavorite,
    viewMode,
  }), [activeActions,
    hasMultipleBuildings,
    hasMultipleGates,
		isFavorite,
		onProductClick,
    pricesVisibility,
    tiers,
		toggleFavorite,
    viewMode])

  const columns = useMemo(() => {
    return buildColumnDefs(listColumns, displayContext)
  }, [listColumns, displayContext])

  // Derive RecordTable sort indicators from hook's sortConfig.
  // The user-visible active sort is the first non-always_apply level with a parameter.
  const userLevel = sortConfig.find(l => !l.always_apply && l.parameter)
  const sortColumnId = userLevel.parameter ?? null
    // ? (PARAM_COLUMN_REMAP[userLevel.parameter] ?? userLevel.parameter)
    // : null
  const sortDirection = userLevel?.direction ?? 'asc'

  // Translate RecordTable column click to a new SortLevel[] for the hook.
  function handleSort(colId, dir) {
    if (!onSortChange) return
    const parameter = colId
    // Keep always_apply levels intact; replace the user-controlled levels.
    const alwaysApplyLevels = sortConfig.filter(l => l.always_apply)
    onSortChange([{ parameter, direction: dir, always_apply: false }, ...alwaysApplyLevels])
  }

  // Scroll active row into view (centered) within RecordTable's scroll container.
  // Uses container.scrollTo instead of el.scrollIntoView to avoid scrolling the page.
  useEffect(() => {
    if (!activeProductKey || !containerRef.current) return
    const scrollContainer = containerRef.current.querySelector('[style*="--rt-scroll-height"]')
    const el = containerRef.current.querySelector(`[data-row-key="${CSS.escape(activeProductKey)}"]`)
    if (!scrollContainer || !el) return

    const containerRect = scrollContainer.getBoundingClientRect()
    const elRect        = el.getBoundingClientRect()
    const targetTop     = scrollContainer.scrollTop + (elRect.top - containerRect.top) - (containerRect.height - elRect.height) / 2

    scrollContainer.scrollTo({ top: targetTop, behavior: 'smooth' })
  }, [activeProductKey])

  return (
    <div className={styles.productList} ref={containerRef}>
      <RecordTable
        columns={columns}
        rows={filteredProducts}
        getRowKey={getRowKey}
        getRowState={getRowState}
        activeRowKey={activeProductKey}
        onRowClick={onProductClick}
        onSort={handleSort}
        sortColumnId={sortColumnId}
        sortDirection={sortDirection}
        stickyColumns={stickyColumns}
        scrollHeight="65vh"
        noDataText={noDataText}
      />
    </div>
  )
}
